import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Icon, Message } from 'semantic-ui-react';
import styled from 'styled-components';
import { CritiqueGroup, useCritiqueFeedback } from 'src/hooks/useCritiqueGroups';
import {
  CritiqueBookPart,
  useCritiqueBookParts,
} from 'src/hooks/useCritiqueBookParts';
import { useNewlyUnlockedPart } from 'src/hooks/useNewlyUnlockedPart';
import { getFeedbackLockState } from 'src/utils/critiqueGroupUtil';
import { MediaQuery } from 'src/constants/size';
import {
  FeedbackReveal,
  UnlockMoment,
} from 'src/components/critiqueGroups/feedbackReveal';
import {
  CritiqueErrorState,
  CritiqueLoadingState,
} from 'src/components/critiqueGroups/critiqueStates';

/**
 * Design cluster G — the member's view of the circle's feedback, chapter by
 * chapter, with the anti-anchoring lock.
 *
 * THE CONTRACT: the server sends only what this member may see and silently
 * drops the rest. So this screen renders `view.comments` / `view.surveyAnswers`
 * as-is and asks `getFeedbackLockState` (i.e. `unlockedParts`, the member's own
 * contributions) whether a chapter is locked. It never counts, hints at, or
 * reconstructs withheld feedback — under `after_own` a locked chapter and an
 * empty one are identical on the wire, and that's the point.
 */

const Layout = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 20px;
  align-items: start;
  ${MediaQuery.phone} {
    grid-template-columns: 1fr;
  }
`;

const ChapterList = styled.div`
  background: var(--br-surface);
  border: 1px solid #e7e3d9;
  border-radius: 13px;
  overflow: hidden;
  max-height: 520px;
  overflow-y: auto;
  ${MediaQuery.phone} {
    max-height: 220px;
  }
`;

const ChapterRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  text-align: left;
  border: none;
  cursor: pointer;
  font: inherit;
  padding: 11px 13px;
  background: ${({ $active }) => ($active ? '#f2fbfa' : 'transparent')};
  border-left: 3px solid ${({ $active }) => ($active ? '#00b5ad' : 'transparent')};
  & + & {
    border-top: 1px solid #f4efe4;
  }
  &:hover {
    background: var(--br-raised-surface);
  }
`;

const ChapterName = styled.span`
  flex: 1;
  font-size: 13px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MemberFeedback: React.FC<{ group: CritiqueGroup }> = ({ group }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const firstBook = (group.books || [])[0];
  const bookId = typeof firstBook === 'string' ? firstBook : firstBook?._id;

  const feedback = useCritiqueFeedback(group._id, bookId);
  const parts = useCritiqueBookParts(bookId);
  const [selected, setSelected] = useState<string | undefined>();
  // The member unlocks by writing in the READER and comes back here, so the
  // unlock beat is detected on return rather than from a mutation.
  const { newlyUnlocked, acknowledge } = useNewlyUnlockedPart(
    group._id,
    feedback.data?.unlockedParts
  );

  /**
   * Chapters to list. Prefer the book's real chapter list; if that read failed,
   * fall back to the parts we can see from the feedback payload itself so the
   * screen degrades to "what's revealed" instead of going blank.
   */
  const chapters = useMemo<CritiqueBookPart[]>(() => {
    if (parts.data && parts.data.length > 0) return parts.data;
    const view = feedback.data;
    const ids = new Set<string>([
      ...(view?.unlockedParts || []),
      ...(view?.comments || []).map(c => String(c.part || '')),
      ...(view?.surveyAnswers || []).map(a => String(a.survey || '')),
    ]);
    ids.delete('');
    return Array.from(ids).map(id => ({ _id: id, title: undefined }));
  }, [parts.data, feedback.data]);

  // A freshly unlocked chapter wins the selection — that's the one they just
  // earned — until they pick another themselves.
  const activeId = selected || newlyUnlocked || chapters[0]?._id;
  const activeChapter = chapters.find(c => c._id === activeId);
  const celebrating = !!newlyUnlocked && !selected && activeId === newlyUnlocked;

  if (feedback.isLoading) return <CritiqueLoadingState />;
  if (feedback.isError) {
    return (
      <CritiqueErrorState
        message={feedback.error?.message}
        onRetry={() => feedback.refetch()}
      />
    );
  }

  if (!bookId) {
    return (
      <Message info data-testid="member-feedback-no-book">
        {t(
          'CritiqueNoBookYetMember',
          'The host hasn’t linked a manuscript yet. Hang tight — reading opens when they do.'
        )}
      </Message>
    );
  }

  const chapterLabel = (c: { _id: string; title?: string }, i: number) =>
    c.title || t('CritiqueChapterN', 'Chapter {{n}}', { n: i + 1 });

  // host_only has no per-chapter lock — one note covers the whole group.
  const hostOnly = feedback.data?.feedbackVisibility === 'host_only';

  if (hostOnly) {
    return (
      <div data-testid="member-feedback">
        <FeedbackReveal view={feedback.data} partId={activeId} />
      </div>
    );
  }

  if (chapters.length === 0) {
    return (
      <Message info data-testid="member-feedback-empty">
        <Icon name="book" />
        {t(
          'CritiqueNothingToShowYet',
          'Nothing to show yet — start reading and leave your thoughts, and the circle’s feedback will appear here.'
        )}
      </Message>
    );
  }

  return (
    <Layout data-testid="member-feedback">
      <ChapterList>
        {chapters.map((c, i) => {
          const state = getFeedbackLockState(feedback.data, c._id);
          return (
            <ChapterRow
              key={c._id}
              $active={c._id === activeId}
              onClick={() => setSelected(c._id)}
              data-testid={`feedback-chapter-${c._id}`}
            >
              <Icon
                name={state === 'locked' ? 'lock' : 'unlock alternate'}
                style={{ color: state === 'locked' ? '#b7b2a6' : '#2fa84f' }}
              />
              <ChapterName>{chapterLabel(c, i)}</ChapterName>
            </ChapterRow>
          );
        })}
      </ChapterList>

      {celebrating ? (
        <UnlockMoment onDone={acknowledge} />
      ) : (
        <FeedbackReveal
          view={feedback.data}
          partId={activeId}
          partLabel={
            activeChapter
              ? chapterLabel(activeChapter, chapters.indexOf(activeChapter))
              : undefined
          }
          onShare={
            bookId
              ? // Sharing happens in the reader (inline comments + surveys) —
                // this feature never re-implements it. The reader is
                // `exact path="/read"` + a `book` QUERY param, so `/read/<id>`
                // would 404.
                () => history.push(`/read?book=${bookId}`)
              : undefined
          }
        />
      )}
    </Layout>
  );
};

export default MemberFeedback;
